import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import LoadingSpinner from '../components/LoadingSpinner';
import Pagination from '../components/Pagination';
import { apiService } from '../services/api';

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest'); // newest, oldest
  const [selectedMainCategory, setSelectedMainCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');

  const currentPage = parseInt(searchParams.get('page')) || 1;
  const mainCategoryId = searchParams.get('main_category');
  const subcategoryId = searchParams.get('subcategory');

  // Ana kategorileri yükle
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await apiService.getMainCategories();
        setCategories(response.data?.main_categories || []);
      } catch (err) {
        console.error('Ana kategoriler yüklenirken hata:', err);
      }
    };
    fetchCategories();
  }, []);

  // Alt kategorileri yükle (ana kategori seçildiğinde)
  useEffect(() => {
    const fetchSubcategories = async () => {
      try {
        if (selectedMainCategory) {
          const response = await apiService.getSubcategories({ main_category_id: selectedMainCategory });
          // categories.php'den gelen veriyi parse et
          const mainCategories = response.data?.main_categories || [];
          const allSubcategories = [];
          
          mainCategories.forEach(mainCat => {
            if (mainCat.categories && mainCat.categories.length > 0) {
              allSubcategories.push(...mainCat.categories);
            }
          });
          
          setSubcategories(allSubcategories);
        } else {
          setSubcategories([]);
        }
      } catch (err) {
        console.error('Alt kategoriler yüklenirken hata:', err);
        setSubcategories([]);
      }
    };
    fetchSubcategories();
  }, [selectedMainCategory]);

  // URL'den kategori değerlerini al
  useEffect(() => {
    if (mainCategoryId) {
      setSelectedMainCategory(mainCategoryId);
    }
    if (subcategoryId) {
      setSelectedSubcategory(subcategoryId);
    }
  }, [mainCategoryId, subcategoryId]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const params = {
          page: currentPage,
          limit: 12,
          sort: sortBy
        };

        if (selectedSubcategory) {
          params.category_id = selectedSubcategory; // Alt kategori ID'si (öncelik)
        } else if (selectedMainCategory) {
          params.main_category_id = selectedMainCategory; // Ana kategori ID'si
        }

        if (searchTerm.trim()) {
          params.search = searchTerm.trim();
        }

        const response = await apiService.getProducts(params);
        const products = response.data?.products || [];
        const pagination = response.data?.pagination || {};
        
        console.log('Products response:', { products: products.length, pagination });
        
        setProducts(products);
        setPagination(pagination);
      } catch (err) {
        console.error('Ürünler yüklenirken hata:', err);
        setError('Ürünler yüklenirken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [currentPage, selectedMainCategory, selectedSubcategory, searchTerm, sortBy]);

  const handleSearch = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const search = formData.get('search');
    setSearchTerm(search);
    const params = { page: 1 };
    if (selectedMainCategory) params.main_category = selectedMainCategory;
    if (selectedSubcategory) params.subcategory = selectedSubcategory;
    setSearchParams(params);
  };

  const handlePageChange = (page) => {
    const params = { page };
    if (selectedMainCategory) params.main_category = selectedMainCategory;
    if (selectedSubcategory) params.subcategory = selectedSubcategory;
    setSearchParams(params);
  };

  const handleMainCategoryFilter = (mainCategoryId) => {
    setSelectedMainCategory(mainCategoryId);
    setSelectedSubcategory(''); // Ana kategori değiştiğinde alt kategoriyi temizle
    const params = { page: 1 };
    if (mainCategoryId) params.main_category = mainCategoryId;
    setSearchParams(params);
  };

  const handleSubcategoryFilter = (subcategoryId) => {
    setSelectedSubcategory(subcategoryId);
    const params = { page: 1 };
    if (selectedMainCategory) params.main_category = selectedMainCategory;
    if (subcategoryId) params.subcategory = subcategoryId;
    setSearchParams(params);
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
  };

  const clearFilters = () => {
    setSelectedMainCategory('');
    setSelectedSubcategory('');
    setSortBy('newest');
    setSearchTerm('');
    setSearchParams({});
  };

  if (loading) {
    return <LoadingSpinner message="Ürünler yükleniyor..." />;
  }

  return (
    <div className="container py-5">
      <div className="row">
        <div className="col-12">
          <h1 className="display-5 fw-bold mb-4">Ürünler</h1>
          
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="mb-4">
            <div className="row">
              <div className="col-md-6">
                <div className="input-group">
                  <input
                    type="text"
                    name="search"
                    className="form-control"
                    placeholder="Ürün ara..."
                    defaultValue={searchTerm}
                  />
                  <button className="btn btn-primary" type="submit">
                    <i className="bi bi-search"></i>
                  </button>
                </div>
              </div>
            </div>
          </form>

          {/* Filters and Sorting */}
          <div className="card filter-card mb-4">
            <div className="card-body filter-section">
              <div className="row align-items-center">
                <div className="col-md-3">
                  <label htmlFor="mainCategoryFilter" className="form-label fw-bold">
                    <i className="bi bi-funnel me-2"></i>Ana Kategori
                  </label>
                  <select
                    id="mainCategoryFilter"
                    className="form-select"
                    value={selectedMainCategory}
                    onChange={(e) => handleMainCategoryFilter(e.target.value)}
                  >
                    <option value="">Tüm Ana Kategoriler</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="col-md-3">
                  <label htmlFor="subcategoryFilter" className="form-label fw-bold">
                    <i className="bi bi-tags me-2"></i>Alt Kategori
                  </label>
                  <select
                    id="subcategoryFilter"
                    className="form-select"
                    value={selectedSubcategory}
                    onChange={(e) => handleSubcategoryFilter(e.target.value)}
                    disabled={!selectedMainCategory || subcategories.length === 0}
                  >
                    <option value="">
                      {!selectedMainCategory 
                        ? "Önce ana kategori seçin" 
                        : subcategories.length === 0 
                          ? "Alt kategori yok" 
                          : "Tüm Alt Kategoriler"
                      }
                    </option>
                    {subcategories.map((subcategory) => (
                      <option key={subcategory.id} value={subcategory.id}>
                        {subcategory.name} ({subcategory.product_count} ürün)
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="col-md-3">
                  <label htmlFor="sortBy" className="form-label fw-bold">
                    <i className="bi bi-sort-down me-2"></i>Sıralama
                  </label>
                  <select
                    id="sortBy"
                    className="form-select"
                    value={sortBy}
                    onChange={(e) => handleSortChange(e.target.value)}
                  >
                    <option value="newest">En Yeni</option>
                    <option value="oldest">En Eski</option>
                  </select>
                </div>
                
                <div className="col-md-3 d-flex align-items-end">
                  <button
                    type="button"
                    className="btn btn-outline-secondary w-100"
                    onClick={clearFilters}
                  >
                    <i className="bi bi-x-circle me-2"></i>
                    Filtreleri Temizle
                  </button>
                </div>
              </div>
              
              {/* Active Filters Display */}
              {(selectedMainCategory || selectedSubcategory || sortBy !== 'newest' || searchTerm) && (
                <div className="mt-3">
                  <small className="text-muted">Aktif filtreler:</small>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {searchTerm && (
                      <span className="badge bg-primary">
                        Arama: "{searchTerm}"
                      </span>
                    )}
                    {selectedMainCategory && (
                      <span className="badge bg-info">
                        Ana Kategori: {categories.find(c => c.id === parseInt(selectedMainCategory))?.name}
                      </span>
                    )}
                    {selectedSubcategory && (
                      <span className="badge bg-success">
                        Alt Kategori: {subcategories.find(c => c.id === parseInt(selectedSubcategory))?.name}
                      </span>
                    )}
                    {sortBy !== 'newest' && (
                      <span className="badge bg-secondary">
                        Sıralama: {
                          sortBy === 'oldest' ? 'En Eski' : 'En Yeni'
                        }
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="alert alert-danger">
              {error}
            </div>
          )}

          {products.length > 0 ? (
            <>
              {/* Results Count */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h5 className="mb-0">
                    {pagination.total || products.length} ürün bulundu
                    {selectedSubcategory && (
                      <small className="text-muted ms-2">
                        ({subcategories.find(c => c.id === parseInt(selectedSubcategory))?.name} kategorisinde)
                      </small>
                    )}
                    {!selectedSubcategory && selectedMainCategory && (
                      <small className="text-muted ms-2">
                        ({categories.find(c => c.id === parseInt(selectedMainCategory))?.name} ana kategorisinde)
                      </small>
                    )}
                  </h5>
                </div>
                {pagination.pages > 1 && (
                  <small className="text-muted">
                    Sayfa {pagination.page} / {pagination.pages}
                  </small>
                )}
              </div>

              <div className="row">
                {products.map((product) => (
                  <div key={product.id} className="col-lg-3 col-md-4 col-sm-6 mb-4">
                    <ProductCard product={product} />
                  </div>
                ))}
              </div>
              
              {pagination.pages > 1 && (
                <Pagination
                  currentPage={pagination.page}
                  totalPages={pagination.pages}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          ) : (
            <div className="text-center py-5">
              <i className="bi bi-music-note-list display-1 text-muted mb-3"></i>
              <h3>Ürün Bulunamadı</h3>
              {selectedSubcategory ? (
                <p className="text-muted">
                  <strong>{subcategories.find(c => c.id === parseInt(selectedSubcategory))?.name}</strong> kategorisinde ürün bulunmuyor.
                  <br />
                  <small>Diğer kategorileri deneyebilir veya filtreleri temizleyebilirsiniz.</small>
                </p>
              ) : selectedMainCategory ? (
                <p className="text-muted">
                  <strong>{categories.find(c => c.id === parseInt(selectedMainCategory))?.name}</strong> ana kategorisinde ürün bulunmuyor.
                  <br />
                  <small>Diğer kategorileri deneyebilir veya filtreleri temizleyebilirsiniz.</small>
                </p>
              ) : searchTerm ? (
                <p className="text-muted">
                  "<strong>{searchTerm}</strong>" araması için sonuç bulunamadı.
                  <br />
                  <small>Farklı anahtar kelimeler deneyebilirsiniz.</small>
                </p>
              ) : (
                <p className="text-muted">
                  Henüz ürün eklenmemiş.
                  <br />
                  <small>Lütfen daha sonra tekrar kontrol edin.</small>
                </p>
              )}
              <div className="mt-4">
                <button
                  type="button"
                  className="btn btn-primary me-2"
                  onClick={clearFilters}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Filtreleri Temizle
                </button>
                <a href="/" className="btn btn-outline-secondary">
                  <i className="bi bi-house me-2"></i>
                  Ana Sayfaya Dön
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Products;
